import React, { useState, useEffect, useRef, useReducer } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/apiClient';
import FilterRow from './SavedFilters/FilterRow';
import CreateFilterModal from './SavedFilters/CreateFilterModal';

// Modal state cluster: open/close, the in-progress name, and which filter is
// being edited always change together, so they live in one reducer.
const initialModalState = {
    isModalOpen: false,
    newFilterName: '',
    editingFilter: null,
};

function modalReducer(state, action) {
    switch (action.type) {
        case 'OPEN_CREATE':
            return { isModalOpen: true, newFilterName: '', editingFilter: null };
        case 'OPEN_RENAME':
            return { isModalOpen: true, newFilterName: action.filter.name, editingFilter: action.filter };
        case 'CLOSE':
            return { ...state, isModalOpen: false };
        case 'SET_NAME':
            return { ...state, newFilterName: action.name };
        case 'RESET':
            return { ...state, newFilterName: '', editingFilter: null };
        default:
            return state;
    }
}

export default function SavedFiltersView({ onViewChange }) {
    const { user } = useAuth();
    const [savedFilters, setSavedFilters] = useState([]);
    const loadingRef = useRef(true);

    // Fetch Filters on Load
    useEffect(() => {
        if (!user) return;

        const fetchFilters = async () => {
            try {
                const data = await api.getFilters();
                setSavedFilters(data.filters || []);
            } catch (err) {
                console.error('Error fetching filters:', err);
            } finally {
                loadingRef.current = false;
            }
        };

        fetchFilters();
    }, [user]);

    // Modal State
    const [modalState, dispatchModal] = useReducer(modalReducer, initialModalState);
    const { isModalOpen, newFilterName, editingFilter } = modalState;
    const isSavingRef = useRef(false);

    const openCreateModal = () => {
        dispatchModal({ type: 'OPEN_CREATE' });
    };

    const openRenameModal = (filter) => {
        dispatchModal({ type: 'OPEN_RENAME', filter });
        setActiveDropdownId(null);
    };

    const handleSaveFilter = async () => {
        if (!newFilterName.trim() || !user) return;
        isSavingRef.current = true;

        if (editingFilter) {
            try {
                await api.updateFilter(editingFilter.id, { name: newFilterName });
                setSavedFilters(savedFilters.map(f =>
                    f.id === editingFilter.id ? { ...f, name: newFilterName } : f
                ));
                dispatchModal({ type: 'CLOSE' });
            } catch (err) {
                console.error('Error updating filter:', err);
                alert('Failed to update filter');
            }
        } else {
            try {
                const data = await api.createFilter({
                    name: newFilterName,
                    notifications: false,
                    status: 'Not Active',
                    criteria: {}
                });
                setSavedFilters([data, ...savedFilters]);
                dispatchModal({ type: 'CLOSE' });
            } catch (err) {
                console.error('Error saving filter:', err);
                alert('Failed to save filter');
            }
        }
        isSavingRef.current = false;
        dispatchModal({ type: 'RESET' });
    };

    // Dropdown Actions
    const [activeDropdownId, setActiveDropdownId] = useState(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (activeDropdownId && !e.target.closest('.filter-actions-menu')) {
                setActiveDropdownId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeDropdownId]);

    const handleDuplicate = async (filter) => {
        if (!user) return;
        setActiveDropdownId(null);

        try {
            const { id, created_at, ...filterData } = filter;
            const data = await api.createFilter({
                ...filterData,
                name: `Copy of ${filter.name}`,
                status: 'Not Active'
            });
            setSavedFilters([data, ...savedFilters]);
        } catch (err) {
            console.error('Error duplicating filter:', err);
            alert('Failed to duplicate filter');
        }
    };

    const handleDelete = async (filterId) => {
        setActiveDropdownId(null);

        try {
            await api.deleteFilter(filterId);
            setSavedFilters(savedFilters.filter(f => f.id !== filterId));
        } catch (err) {
            console.error('Error deleting filter:', err);
            alert('Failed to delete filter');
        }
    };

    return (
        <div className="max-w-[95%] mx-auto w-full pt-6 relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Job Filters</h1>
                <button
                    type="button"
                    onClick={openCreateModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add filter
                </button>
            </div>

            {/* Filters Table */}
            {/* Filters Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
                <table className="w-full">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/3">Name</th>
                            <th className="text-center py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Notifications</th>
                            <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="py-4 px-6 w-48" aria-label="Actions"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {savedFilters.map((filter, index) => {
                            // Check if filter is setup (has criteria) OR is explicitly active
                            const isSetup = filter.status === 'Active' || Object.keys(filter.criteria || {}).length > 0;
                            const isLastTwo = index >= savedFilters.length - 2;

                            return (
                                <FilterRow
                                    key={filter.id}
                                    filter={filter}
                                    isSetup={isSetup}
                                    isLastTwo={isLastTwo}
                                    isDropdownOpen={activeDropdownId === filter.id}
                                    onViewChange={onViewChange}
                                    onToggleDropdown={() => setActiveDropdownId(activeDropdownId === filter.id ? null : filter.id)}
                                    onRename={openRenameModal}
                                    onDuplicate={handleDuplicate}
                                    onDelete={handleDelete}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Create Filter Modal */}
            {isModalOpen && (
                <CreateFilterModal
                    editingFilter={editingFilter}
                    newFilterName={newFilterName}
                    onNameChange={(name) => dispatchModal({ type: 'SET_NAME', name })}
                    onClose={() => dispatchModal({ type: 'CLOSE' })}
                    onSave={handleSaveFilter}
                />
            )}
        </div>
    );
}
